
import {
  AppPushNotificationsManagerProps,
  AppPushNotificationsProps,
  AppPushPermissionStatus,
} from '@common/core/capacitor/app-push-notifications/assets/app-push-notifications-interface';
import { AppPushNotificationsMobile } from '@common/core/capacitor/app-push-notifications/app-push-notification-mobile';
import { AppPushNotificationsWeb } from '@common/core/capacitor/app-push-notifications/app-push-notification-web';
// import { AppPushNotificationsElectron } from '@common/core/capacitor/app-push-notifications/app-push-notification-electron'; // optionnel
import { isMobileApp, isElectronApp } from '@common/core/platform-detection';
import { apiClient } from '@common/http/query-client';

export let AppPushRouter: { navigate?: (url: string) => void } = {};

/**
 * Classe "point d'entrée" qui va router vers la bonne implémentation
 * (Mobile, Web, Electron...) en fonction de la plateforme.
 */
class AppPushNotificationsManager implements AppPushNotificationsManagerProps {
  private manager: AppPushNotificationsProps|undefined;
  private loadPromise: Promise<void> | null = null;
  private managerPlatform: 'electron' | 'mobile' | 'web' | null = null;
  private tools: Record<string, any> = {
    delay: (ms: number) => new Promise(resolve => setTimeout(resolve, ms)),
    retry: async (fn: () => Promise<any>, retries = 3, delayMs = 1000) => {
      for (let i = 0; i < retries; i++) {
        try {
          const result = await fn();
          if (result) return result;
        } catch (error) {
          // console.error(`Tentative ${i + 1} échouée :`, error);
        }
        await this.tools.delay(delayMs);
      }
      return null;
    },
    getVapidKey: async (): Promise<string | undefined> => {
      return await this.tools.retry(async () => {
        const data = await apiClient.get('push/key').then(response => response.data);
        return data.vapidPublicKey ?? null;
      }, 3, 1000, 'getVapidKey');
    },
    storeToken: async (token: string): Promise<void> => {
      return await this.tools.retry(async () => {
        const data = await apiClient.post('push/fcm/token',{token: token}).then(response => response.data);
        return data;
      }, 3, 1000, 'storeToken');
    },
    deleteToken: async (token: string): Promise<void> => {
      return await this.tools.retry(async () => {
        const data = await apiClient.delete(`push/fcm/token?token=${token}`).then(response => response.data);
        return data;
      }, 3, 1000, 'deleteToken');
    },
    getUnreadNotifications: async () => {
      return await this.tools.retry(async () => {
        const data = await apiClient.get('notifications/count').then(response => response.data);
        return data.unreadCount;
      }, 3, 1000, 'getUnreadNotifications');
    }
  }

  constructor() {
    this.manager = undefined;
  }

  private getCurrentPlatform(): 'electron' | 'mobile' | 'web' {
    if (isElectronApp()) {
      return 'electron';
    }

    return isMobileApp() ? 'mobile' : 'web';
  }

  public async loadManager(): Promise<void> {
    const platform = this.getCurrentPlatform();

    if (this.managerPlatform !== platform) {
      this.loadPromise = null;
      this.managerPlatform = platform;

      if (platform === 'electron') {
        // Cas Electron
        // this.manager = new AppPushNotificationsElectron(this.tools);
        this.manager = undefined;
      } else if (platform === 'mobile') {
        // Cas Mobile/Tablet (Capacitor)
        this.manager = new AppPushNotificationsMobile(this.tools);
      } else {
        // Cas Web
        this.manager = new AppPushNotificationsWeb(this.tools);
      }
    }

    if (!this.loadPromise) {
      this.loadPromise = this.manager?.loadManager() ?? Promise.resolve();
    }

    await this.loadPromise;
  }
  
  /**
   * Appelle la méthode init() de l'implémentation sous-jacente
   */
  public async init(): Promise<void> {
    return await this.manager?.init();
  }

  /**
   * Appelle la méthode syncToken() de l'implémentation sous-jacente
   */
  public async syncToken(): Promise<void> {
    return this.manager?.syncToken();
  }

  /**
   * Appelle la méthode uninit() de l'implémentation sous-jacente
   */
  public async uninit(): Promise<void> {
    return this.manager?.uninit();
  }

  /**
   * Demande la permission de notifications (si besoin)
   */
  public async requestPermission(): Promise<Record<string, any>> {
    return this.manager?.requestPermission() ?? { granted: false, status: "unknown" };
  }

  /**
   * Demande la permission de notifications (si besoin)
   */
  public async hasPermission(): Promise<boolean|null|undefined> {
    return this.manager?.hasPermission();
  }

  /**
   * Récupère l'état détaillé de permission de notification.
   */
  public async getPermissionStatus(): Promise<AppPushPermissionStatus> {
    return (await this.manager?.getPermissionStatus()) ?? 'unknown';
  }

  /**
   * Vérifie si la permission de notification a déjà été demandée
   * @returns {boolean} true si la permission a été demandée, false sinon
   */
  public async hasRequestedPermission(): Promise<boolean|null|undefined> {
   return this.manager?.hasRequestedPermission();
  }

  /**
   * Met à jour le localStorage pour indiquer que la permission a été demandée
   */
  public async setRequestedPermission(value: boolean = true): Promise<void> {
   return this.manager?.setRequestedPermission(value);
  }

  /**
   * Récupère le token push (FCM ou autre)
   */
  public async getToken(): Promise<string|null|undefined> {
    return this.manager?.getToken();
  }

  /**
   * Souscrit l'appareil / le navigateur à un topic
   */
  public async subscribeToTopic(topicName: string): Promise<void> {
    return apiClient.post('user/notifications/topic', { topic: topicName });
  }

  /**
   * Se désinscrit du topic
   */
  public async unsubscribeFromTopic(topicName: string): Promise<void> {
    return apiClient.delete('user/notifications/topic', {
      data: { topic: topicName },
    });
  }

  public async togglesubscribe(topic: string, follow: boolean): Promise<void> {
    if (follow) {
      await this.subscribeToTopic(topic);
    } else {
      await this.unsubscribeFromTopic(topic);
    }
  }

  public isNotificationSupported(): boolean {
    return this.manager?.notificationIsSupported ?? false;
  }

  public openConfig(): Promise<void> {
    return this.manager?.openConfig() ?? Promise.resolve();
  }
}

export const appPushNotificationManager = new AppPushNotificationsManager();

